import { Calendar } from 'react-date-range';

import { FC, Fragment, ReactNode, useCallback, useState, useEffect, useRef } from 'react';
import { useClickAway } from 'hooks/useClickAway';
import MovablePlaceholder from '../MovablePlaceholder/MovablePlaceholder';
import { ValidationBody } from 'ts/interfaces/Validation';
import { addDays } from 'utils/utils';
import { getErrorInField } from 'utils/validation';
import { dateFormat, getLocaleSet } from 'utils/ui';
import LoaderPoints from '../LoaderPoints/LoaderPoints';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';
import withErrorBoundary from 'components/ui/withErrorBoundaries';
import { createPortal } from 'react-dom';

interface FormGroupDatepickerProps {
  onChange: (item: Date) => void;
  className?: string;
  value?: Date;
  label: string;
  disabled?: boolean;
  maxDate?: Date;
  minDate?: Date;
  longFormat?: boolean;
  disabledDates?: Date[];
  dayContentRenderer?: (date: Date) => ReactNode;
  movablePlaceholder?: string;
  movablePlaceholderIcon?: any;
  movablePlaceholderSm?: boolean;
  validation?: ValidationBody;
  isSubmitted?: boolean;
  isLoading?: boolean;
  onClickReset?: () => void;
  vertical?: boolean;
  appendToBody?: boolean;
  scrollOnFocusMobile?: boolean;
}

const FormGroupDatepicker: FC<FormGroupDatepickerProps> = ({ className = '', ...props }) => {
  const [open, setOpen] = useState(false);
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const calendarRef = useClickAway(() => closeDatepicker());
  const { t } = useTranslation();
  const format = "EEE '<span class=''text-bold text-uppercase''>'dd MMM'</span>,' yy";
  const inputRef = useRef<HTMLButtonElement>(null);
  const initScrollPosition = useRef(0);
  const openDatepicker = () => {
    setOpen(true);
    if (props.disabled) {
      return;
    }

    if (window.innerWidth < 768 && props.scrollOnFocusMobile && inputRef.current) {
      initScrollPosition.current = window.scrollY;
      const offset = 100;
      const elementPosition = inputRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };
  const closeDatepicker = () => {
    setOpen(false);

    if (window.innerWidth < 768 && props.scrollOnFocusMobile && initScrollPosition.current) {
      window.scrollTo({ top: initScrollPosition.current, behavior: 'smooth' });
      initScrollPosition.current = 0;
    }
  };

  // Safely remove Hebrew text from calendar day headers
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (!open) return;

        const days = document.querySelectorAll('.rdrCalendarWr .rdrWeekDay');
        if (days) {
          days.forEach((day: Element) => {
            if (day.innerHTML.includes('יום')) {
              day.innerHTML = day.innerHTML.replace('יום', '');
            }
          });
        }
      }, 100); // Small delay to ensure calendar is rendered

      return () => {
        clearTimeout(timer);
      };
    }
  }, [open]);

  const renderCalendar = () => (
    <>
      <Calendar
        minDate={props.minDate}
        maxDate={props.maxDate ? props.maxDate : addDays(730)}
        date={props.value}
        color={colorContrast}
        disabledDates={props.disabledDates}
        dayContentRenderer={props.dayContentRenderer}
        onChange={(item) => {
          props.onChange(item);
          closeDatepicker();
        }}
        locale={getLocaleSet()}
      />

      <div className="daterangepicker-bottom">
        <div className="flex-column mb-15">
          <div className="text-light mb-5">{t('search.check_in')}</div>
          <div className="text-bold">{dateFormat(props.value, 'EEE - dd MMM, yyyy')}</div>
        </div>
        <div className="flex-row">
          <Button className="btn btn-primary mr-a" title={t('choose')} onClick={closeDatepicker} />
          {props.onClickReset && (
            <Button title={t('reset')} className="btn btn-default" onClick={props.onClickReset} />
          )}
        </div>
      </div>
    </>
  );

  const isValid = useCallback(() => {
    return getErrorInField(
      props.validation!,
      dateFormat(props.value, 'yyyy-MM-dd') || '',
      props.isSubmitted!
    );
  }, [props.value, props.isSubmitted]); // eslint-disable-line react-hooks/exhaustive-deps


  const wrClassName = `form-group form-group-calendar ${className}${isValid() ? ' is-invalid' : ''
    }${props.movablePlaceholder ? ' mb-0' : ''} ${props.vertical ? 'form-group-calendar-vertical form-group-calendar-vertical-single' : ''
    }${props.movablePlaceholderSm ? ' movable-placeholder-datepicker-sm' : ''}`;

  const inputClassName = `form-control text-start${props.movablePlaceholder ? ' movable-placeholder-input' : ''
    }`;

  return (
    <div className={wrClassName}>
      <LoaderPoints isLoading={props.isLoading} />
      {!props.movablePlaceholder && !props.vertical && (
        <label>
          {props.label} {props.validation?.required && <span className="color-danger">*</span>}
        </label>
      )}
      <button
        type="button"
        ref={inputRef}
        className={inputClassName}
        disabled={props.disabled}
        onClick={openDatepicker}
      >
        {!!props.value && !!props.longFormat && (
          <span dangerouslySetInnerHTML={{ __html: dateFormat(props.value, format) }}></span>
        )}
        {!!props.value && !props.longFormat && (
          <span key={props.value?.getTime()}>{dateFormat(props.value, 'dd-MMM-yyyy')}</span>
        )}
      </button>

      {props.vertical && (
        <Fragment>
          <div className="form-group-calendar-vertical-big">{dateFormat(props.value, 'dd')}</div>
          <div className="form-group-calendar-vertical-dates">
            <div className="text-bold">
              {dateFormat(props.value, 'MMM')
                .split('')
                .map((m, i) => (
                  <span key={i}>{m}</span>
                ))}
            </div>
            <div>
              {dateFormat(props.value, 'eee')
                .split('')
                .map((m, i) => (
                  <span key={i}>{m}</span>
                ))}
            </div>
          </div>
        </Fragment>
      )}
      {!!isValid() && <div className="form-invalid-text">{isValid()}</div>}

      {props.movablePlaceholder && (
        <MovablePlaceholder
          label={props.movablePlaceholder}
          showSmall={open || !!props.value}
          icon={props.movablePlaceholderIcon}
        />
      )}
      {open && (
        <>
          {props.appendToBody ? (
            createPortal(
              <div
                ref={calendarRef}
                className="rdrCalendarWr"
                style={{
                  position: 'fixed',
                  zIndex: 9999,
                  top: inputRef.current ? inputRef.current.getBoundingClientRect().bottom + 5 : 0,
                  left: inputRef.current ? inputRef.current.getBoundingClientRect().left : 0,
                  border: '1px solid var(--color-border)',
                }}
              >
                {renderCalendar()}
              </div>,
              document.body
            )
          ) : (
            <div ref={calendarRef} className="rdrCalendarWr">
              {renderCalendar()}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default withErrorBoundary(FormGroupDatepicker, 'FormGroupDatepicker');
